import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const LoginForm = ({ onSubmit, onQuickLogin, loading, error }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberDevice: false,
    sessionDuration: '8',
    autoLogin: false
  });

  // Check if mock auth mode is enabled
  const isMockAuthMode = import.meta.env?.VITE_MOCK_AUTH_MODE === 'true';

  // Load saved credentials on mount
  useEffect(() => {
    const savedCredentials = localStorage.getItem('ba_auto_login');
    if (savedCredentials) {
      try {
        const credentials = JSON.parse(savedCredentials);
        setFormData(prev => ({
          ...prev,
          email: credentials?.email || '',
          password: credentials?.password || '',
          autoLogin: true,
          rememberDevice: true
        }));
      } catch (err) {
        console.error('Failed to load saved credentials:', err);
      }
    }
  }, []);

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    // Save credentials if auto-login is enabled
    if (formData?.autoLogin) {
      const credentials = {
        email: formData?.email,
        password: formData?.password
      };
      localStorage.setItem('ba_auto_login', JSON.stringify(credentials));
    } else {
      // Remove saved credentials if auto-login is disabled
      localStorage.removeItem('ba_auto_login');
    }
    
    onSubmit?.(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAutoLoginToggle = (checked) => {
    handleChange('autoLogin', checked);
    if (!checked) {
      // Clear saved credentials when disabling auto-login
      localStorage.removeItem('ba_auto_login');
    }
  };

  const handleQuickLogin = (e) => {
    e?.preventDefault();
    onQuickLogin?.();
  };

  return (
    <div className="space-y-4">
      {/* Mock Auth Mode Indicator */}
      {isMockAuthMode && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
          <Icon name="AlertTriangle" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-900 font-semibold mb-1">
              🚧 TEST-MODUS AKTIV
            </p>
            <p className="text-xs text-amber-800">
              Mock-Authentifizierung ist aktiviert. Supabase wird umgangen. 
              Jede Anmeldung funktioniert ohne echte Prüfung. 
              <strong className="block mt-1">Vor Produktion: VITE_MOCK_AUTH_MODE=false setzen!</strong>
            </p>
          </div>
        </div>
      )}

      {/* Quick Login Button - Prominent placement */}
      <Button
        onClick={handleQuickLogin}
        disabled={loading}
        className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold py-3"
      >
        {loading ? (
          <>
            <Icon name="Loader2" size={20} className="animate-spin" />
            Anmeldung läuft...
          </>
        ) : (
          <>
            <Icon name="Zap" size={20} />
            Schnellanmeldung ohne Passwort
          </>
        )}
      </Button>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-slate-500">Oder mit Anmeldedaten</span>
        </div>
      </div>

      {/* Traditional Login Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">
            E-Mail-Adresse
          </label>
          <Input
            type="email"
            value={formData?.email}
            onChange={(e) => handleChange('email', e?.target?.value)}
            placeholder="ihre.email@arbeitsagentur.de"
            disabled={loading}
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">
            Passwort
          </label>
          <Input
            type="password"
            value={formData?.password}
            onChange={(e) => handleChange('password', e?.target?.value)}
            placeholder="••••••••"
            disabled={loading}
            className="w-full"
          />
        </div>

        <div className="space-y-3">
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData?.autoLogin}
                onChange={(e) => handleAutoLoginToggle(e?.target?.checked)}
                disabled={loading}
                className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
              />
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-700 font-medium">Automatisch anmelden</span>
                <div className="group relative">
                  <Icon name="Info" size={14} className="text-slate-400 hover:text-slate-600" />
                  <div className="invisible group-hover:visible absolute left-0 top-6 w-64 p-2 bg-slate-800 text-white text-xs rounded shadow-lg z-10">
                    Ihre Anmeldedaten werden sicher gespeichert und beim nächsten Besuch automatisch ausgefüllt
                  </div>
                </div>
              </div>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData?.rememberDevice}
                onChange={(e) => handleChange('rememberDevice', e?.target?.checked)}
                disabled={loading}
                className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
              />
              <span className="text-sm text-slate-700">Gerät merken</span>
            </label>
          </div>

          <div className="flex items-center justify-end">
            <button
              type="button"
              className="text-sm text-orange-600 hover:text-orange-700 hover:underline"
              disabled={loading}
            >
              Passwort vergessen?
            </button>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Sitzungsdauer
            </label>
            <select
              value={formData?.sessionDuration}
              onChange={(e) => handleChange('sessionDuration', e?.target?.value)}
              disabled={loading}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="2">2 Stunden</option>
              <option value="4">4 Stunden</option>
              <option value="8">8 Stunden (Standard)</option>
              <option value="24">24 Stunden</option>
            </select>
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full"
        >
          {loading ? (
            <>
              <Icon name="Loader2" size={20} className="animate-spin" />
              Anmeldung läuft...
            </>
          ) : (
            <>
              <Icon name="LogIn" size={20} />
              Mit Passwort anmelden
            </>
          )}
        </Button>

        {formData?.autoLogin && (
          <div className="flex items-start gap-2 p-3 bg-teal-50 border border-teal-200 rounded-lg">
            <Icon name="ShieldCheck" size={16} className="text-teal-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-teal-800">
              Automatische Anmeldung aktiviert. Ihre Daten werden sicher auf diesem Gerät gespeichert.
            </p>
          </div>
        )}
      </form>

      {/* Quick Login Info */}
      <div className="flex items-start gap-2 p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
        <Icon name="Info" size={16} className="text-cyan-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-xs text-cyan-800 font-medium mb-1">
            Schnellanmeldung aktiviert
          </p>
          <p className="text-xs text-cyan-700">
            Verwenden Sie die Schnellanmeldung für sofortigen Zugriff ohne Eingabe von Anmeldedaten. 
            Ideal für vertrauenswürdige Geräte und sichere Umgebungen.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;